/**
 * Dashboard - Network Overview
 * Professional enterprise dashboard design
 */

import { useState, useEffect } from 'react';
import { 
  useNetworkMetrics, 
  useTopology, 
  useCircuits,
} from '../hooks/useHttpPolling';
import './Dashboard.css';

// Global network economics data type
interface NetworkEconomics {
  transactionsToday: number;
  tps: number;
  tokenVolume24h: number;
  tokenValueUsd: number;
  activeAccounts: number;
  gasBurned: number;
  gasBurnedUsd: number;
}

// SVG Icons for metrics
const MetricIcons = {
  Messages: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  ),
  Nodes: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="4" y="4" width="6" height="6" rx="1" />
      <rect x="14" y="4" width="6" height="6" rx="1" />
      <rect x="4" y="14" width="6" height="6" rx="1" />
      <rect x="14" y="14" width="6" height="6" rx="1" />
    </svg>
  ),
  Connections: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="6" cy="6" r="3" />
      <circle cx="18" cy="18" r="3" />
      <path d="M8.5 8.5L15.5 15.5" />
    </svg>
  ),
  Latency: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 6v6l4 2" />
    </svg>
  ),
  Circuits: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M4 12h4M16 12h4M12 4v4M12 16v4" />
      <circle cx="12" cy="12" r="4" />
    </svg>
  ),
  Proposals: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M12 2l8 4v6c0 5-3.5 9-8 11-4.5-2-8-6-8-11V6l8-4z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  ),
  Arrow: () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M4 8h8M9 5l3 3-3 3" />
    </svg>
  ),
  Transaction: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M17 3l4 4-4 4M3 7h18M7 21l-4-4 4-4M21 17H3" />
    </svg>
  ),
  TPS: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
    </svg>
  ),
  Volume: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
    </svg>
  ),
  Value: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M2 12l5 5 5-10 5 10 5-5" />
    </svg>
  ),
  Accounts: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
    </svg>
  ),
  Gas: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M14 4h2a2 2 0 012 2v14a2 2 0 01-2 2H8a2 2 0 01-2-2V6a2 2 0 012-2h2" />
      <path d="M12 2v4M9 12h6M9 16h6" />
    </svg>
  ),
};

export default function Dashboard() {
  return (
    <div className="dashboard animate-slide-up">
      {/* Header with quick actions */}
      <div className="dashboard-header">
        <div className="dashboard-header-left">
          <h2 className="dashboard-subtitle">Network Health</h2>
          <span className="dashboard-status">
            <span className="status-dot healthy"></span>
            All systems operational
          </span>
        </div>
        <div className="dashboard-header-right">
          <button className="btn btn-secondary btn-sm">
            <MetricIcons.Arrow />
            Export Report
          </button>
        </div>
      </div>

      {/* Metrics Overview */}
      <section className="metrics-section">
        <MetricsGrid />
      </section>
      
      {/* Network Economics */}
      <section className="economics-section">
        <div className="section-header">
          <h3 className="section-title">Global Lattice Economics</h3>
          <span className="section-subtitle">Real-time network transaction data</span>
        </div>
        <NetworkEconomicsGrid />
      </section>
      
      {/* Two column layout */}
      <div className="dashboard-grid">
        {/* Quick Topology */}
        <section className="card">
          <div className="card-header">
            <div className="card-title">
              <span className="card-title-icon">
                <MetricIcons.Nodes />
              </span>
              Active Nodes
            </div>
            <a href="/topology" className="card-action">
              View All
              <MetricIcons.Arrow />
            </a>
          </div>
          <div className="card-body">
            <TopologyQuickView />
          </div>
        </section>
        
        {/* Recent Activity */}
        <section className="card">
          <div className="card-header">
            <div className="card-title">
              <span className="card-title-icon">
                <MetricIcons.Circuits />
              </span>
              Recent Activity
            </div>
            <a href="/circuits" className="card-action">
              View All
              <MetricIcons.Arrow />
            </a>
          </div>
          <div className="card-body">
            <RecentActivity />
          </div>
        </section>
      </div>
    </div>
  );
}

function MetricsGrid() {
  const { data: metrics, loading, error } = useNetworkMetrics();
  
  if (loading && !metrics) {
    return (
      <div className="metrics-grid">
        {[1,2,3,4,5,6].map(i => (
          <div key={i} className="metric-card skeleton-card">
            <div className="skeleton" style={{ width: 40, height: 40, borderRadius: 8 }} />
            <div className="metric-content">
              <div className="skeleton" style={{ width: 60, height: 24, marginBottom: 4 }} />
              <div className="skeleton" style={{ width: 80, height: 14 }} />
            </div>
          </div>
        ))}
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="metrics-error">
        <span className="error-icon">!</span>
        <span>Unable to load metrics</span>
      </div>
    );
  }
  
  if (!metrics) {
    return <div className="empty-state">No metrics available</div>;
  }
  
  return (
    <div className="metrics-grid">
      <MetricCard 
        label="Messages/sec" 
        value={formatBigNumber(metrics.messagesPerSecond)}
        icon={<MetricIcons.Messages />}
        color="blue"
      />
      <MetricCard 
        label="Active Nodes" 
        value={metrics.totalNodes.toString()}
        icon={<MetricIcons.Nodes />}
        color="purple"
      />
      <MetricCard 
        label="Connections" 
        value={metrics.totalConnections.toString()}
        icon={<MetricIcons.Connections />}
        color="cyan"
      />
      <MetricCard 
        label="Avg Latency" 
        value={`${metrics.avgLatencyMs}ms`}
        icon={<MetricIcons.Latency />}
        color={metrics.avgLatencyMs < 100 ? 'green' : metrics.avgLatencyMs < 300 ? 'yellow' : 'red'}
        status={metrics.avgLatencyMs < 100 ? 'good' : metrics.avgLatencyMs < 300 ? 'warning' : 'error'}
      />
      <MetricCard 
        label="Active Circuits" 
        value={metrics.activeCircuits.toString()}
        icon={<MetricIcons.Circuits />}
        color="orange"
      />
      <MetricCard 
        label="Pending Proposals" 
        value={metrics.pendingProposals.toString()}
        icon={<MetricIcons.Proposals />}
        color={metrics.pendingProposals > 0 ? 'yellow' : 'green'}
        status={metrics.pendingProposals > 0 ? 'warning' : undefined}
      />
    </div>
  );
}

function MetricCard({ 
  label, 
  value, 
  icon, 
  color = 'blue',
  status,
}: { 
  label: string; 
  value: string; 
  icon: React.ReactNode;
  color?: 'blue' | 'purple' | 'cyan' | 'green' | 'yellow' | 'orange' | 'red';
  status?: 'good' | 'warning' | 'error';
}) {
  return (
    <div className={`metric-card metric-${color} ${status ? `status-${status}` : ''}`}>
      <div className={`metric-icon-wrapper`}>
        {icon}
      </div>
      <div className="metric-content">
        <span className="metric-value">{value}</span>
        <span className="metric-label">{label}</span>
      </div>
    </div>
  );
}

function TopologyQuickView() {
  const { data: topology, loading, error } = useTopology();
  
  if (loading && !topology) {
    return (
      <div className="node-list">
        {[1,2,3].map(i => (
          <div key={i} className="node-item skeleton-item">
            <div className="skeleton" style={{ width: 10, height: 10, borderRadius: '50%' }} />
            <div className="skeleton" style={{ flex: 1, height: 16 }} />
            <div className="skeleton" style={{ width: 50, height: 20, borderRadius: 10 }} />
          </div>
        ))}
      </div>
    );
  }
  
  if (error) {
    return <div className="list-error">Unable to load topology</div>;
  }
  
  if (!topology) {
    return <div className="empty-state-small">No topology data</div>;
  }
  
  return (
    <div className="node-list">
      {topology.nodes.slice(0, 5).map((node) => (
        <div key={node.id} className="node-item">
          <span className={`status-dot status-dot-lg ${node.status.toLowerCase()}`} />
          <div className="node-info">
            <span className="node-id">{node.id}</span>
            <span className="node-region">{node.region}</span>
          </div>
          <span className={`badge badge-${node.type === 'scatter' ? 'primary' : 'default'}`}>
            {node.type}
          </span>
        </div>
      ))}
      {topology.nodes.length === 0 && (
        <div className="empty-state-small">
          <p>No nodes connected</p>
          <a href="/infrastructure" className="btn btn-sm btn-secondary">Deploy Node</a>
        </div>
      )}
    </div>
  );
}

function RecentActivity() {
  const { data: circuits, loading, error } = useCircuits();
  
  if (loading && !circuits) {
    return (
      <div className="activity-list">
        {[1,2,3].map(i => (
          <div key={i} className="activity-item skeleton-item">
            <div className="skeleton" style={{ width: 60, height: 20, borderRadius: 10 }} />
            <div className="skeleton" style={{ flex: 1, height: 16 }} />
            <div className="skeleton" style={{ width: 50, height: 14 }} />
          </div>
        ))}
      </div>
    );
  }
  
  if (error) {
    return <div className="list-error">Unable to load activity</div>;
  }
  
  if (!circuits) {
    return <div className="empty-state-small">No activity data</div>;
  }
  
  const allItems = [
    ...(circuits.pending || []).slice(0, 3).map((p: unknown) => ({ 
      ...(p as object), 
      status: 'pending' as const
    })),
    ...(circuits.deployed || []).slice(0, 3).map((d) => ({ 
      id: d.circuitId, 
      type: d.type, 
      status: 'deployed' as const
    })),
  ];
  
  if (allItems.length === 0) {
    return (
      <div className="empty-state-small">
        <p>No recent activity</p>
        <a href="/circuits" className="btn btn-sm btn-secondary">View Circuits</a>
      </div>
    );
  }
  
  return (
    <div className="activity-list">
      {allItems.map((item, i) => (
        <div key={i} className="activity-item">
          <span className={`badge badge-${item.status === 'pending' ? 'warning' : 'success'}`}>
            {item.status}
          </span>
          <span className="activity-type">{(item as { type?: string }).type || 'Circuit'}</span>
          <span className="activity-time">Just now</span>
        </div>
      ))}
    </div>
  );
}

function NetworkEconomicsGrid() {
  const [economics, setEconomics] = useState<NetworkEconomics | null>(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchEconomics = async () => {
      try {
        const edgeUrl = import.meta.env.VITE_EDGE_URL || 'https://edge.estream.dev';
        const response = await fetch(`${edgeUrl}/api/metrics/economics`);
        if (response.ok) {
          const data = await response.json();
          setEconomics(data);
        } else {
          // Mock data for development
          setMockEconomics();
        }
      } catch {
        setMockEconomics();
      } finally {
        setLoading(false);
      }
    };
    
    const setMockEconomics = () => {
      setEconomics({
        transactionsToday: 1_234_567,
        tps: 847,
        tokenVolume24h: 12_500_000,
        tokenValueUsd: 2.47,
        activeAccounts: 45_892,
        gasBurned: 1_234_567,
        gasBurnedUsd: 3_050_860,
      });
    };
    
    fetchEconomics();
    // Refresh every 10 seconds
    const interval = setInterval(fetchEconomics, 10000);
    return () => clearInterval(interval);
  }, []);
  
  if (loading) {
    return (
      <div className="economics-grid">
        {[1, 2, 3, 4, 5, 6].map(i => (
          <div key={i} className="economics-card skeleton-card">
            <div className="skeleton" style={{ width: 40, height: 40, borderRadius: 8 }} />
            <div className="economics-content">
              <div className="skeleton" style={{ width: 80, height: 28, marginBottom: 4 }} />
              <div className="skeleton" style={{ width: 100, height: 14 }} />
            </div>
          </div>
        ))}
      </div>
    );
  }
  
  if (!economics) {
    return <div className="empty-state">Unable to load economics data</div>;
  }
  
  return (
    <div className="economics-grid">
      <div className="economics-card">
        <div className="economics-icon icon-transactions">
          <MetricIcons.Transaction />
        </div>
        <div className="economics-content">
          <span className="economics-value">{formatBigNumber(economics.transactionsToday)}</span>
          <span className="economics-label">Transactions Today</span>
        </div>
      </div>
      
      <div className="economics-card">
        <div className="economics-icon icon-tps">
          <MetricIcons.TPS />
        </div>
        <div className="economics-content">
          <span className="economics-value">{economics.tps.toLocaleString()}</span>
          <span className="economics-label">TPS (real-time)</span>
        </div>
      </div>
      
      <div className="economics-card">
        <div className="economics-icon icon-volume">
          <MetricIcons.Volume />
        </div>
        <div className="economics-content">
          <span className="economics-value">${formatBigNumber(economics.tokenVolume24h)}</span>
          <span className="economics-label">Token Volume (24h)</span>
        </div>
      </div>
      
      <div className="economics-card">
        <div className="economics-icon icon-value">
          <MetricIcons.Value />
        </div>
        <div className="economics-content">
          <span className="economics-value">${economics.tokenValueUsd.toFixed(2)}</span>
          <span className="economics-label">Token Value (USD)</span>
        </div>
      </div>
      
      <div className="economics-card">
        <div className="economics-icon icon-accounts">
          <MetricIcons.Accounts />
        </div>
        <div className="economics-content">
          <span className="economics-value">{formatBigNumber(economics.activeAccounts)}</span>
          <span className="economics-label">Active Accounts</span>
        </div>
      </div>
      
      <div className="economics-card">
        <div className="economics-icon icon-gas">
          <MetricIcons.Gas />
        </div>
        <div className="economics-content">
          <span className="economics-value">${formatBigNumber(economics.gasBurnedUsd)}</span>
          <span className="economics-label">Gas/Fees Burned</span>
        </div>
      </div>
    </div>
  );
}

function formatBigNumber(n: bigint | number | string): string {
  const num = typeof n === 'string' ? parseInt(n, 10) : typeof n === 'bigint' ? Number(n) : n;
  if (isNaN(num)) return '0';
  if (num >= 1_000_000) return `${(num / 1_000_000).toFixed(1)}M`;
  if (num >= 1_000) return `${(num / 1_000).toFixed(1)}K`;
  return num.toString();
}
